import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell,
} from "recharts";
import {
  ClipboardList, Wrench, Coins, AlertTriangle, TrendingUp, Users, Bus,
  Repeat2, ClipboardCheck, CalendarDays,
} from "lucide-react";
import OperationalNotesWidget from "@/components/OperationalNotesWidget";
import { useAuth } from "@/context/AuthContext";

const ACCENT = "#FF7A1A";
const SUCCESS = "#3DD68C";
const INDIGO = "#8B9CFF";
const WARN = "#F2C94C";
const DANGER = "#FF5C5C";
const PALETTE = [ACCENT, SUCCESS, INDIGO, WARN, DANGER, "#54e6a0"];

const fmtTl = (n) => `₺${Number(n || 0).toLocaleString("tr-TR")}`;
const fmtInt = (n) => Number(n || 0).toLocaleString("tr-TR");
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("tr-TR") : "—";

export default function Dashboard() {
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = role === "admin";
  const isDE = role === "data_entry";

  const [summary, setSummary] = useState(null);
  const [mechanics, setMechanics] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [settings, setSettings] = useState(null);
  const [pool, setPool] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [notes, setNotes] = useState([]);
  const [shiftsToday, setShiftsToday] = useState([]);
  const [accidents, setAccidents] = useState([]);
  const [docWarnings, setDocWarnings] = useState([]);
  const [unmarkedField, setUnmarkedField] = useState(null);

  useEffect(() => {
    if (!role) return;
    (async () => {
      const d = new Date();
      const currentMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const today = d.toISOString().slice(0, 10);
      try {
        const [s, m, v, cfg, wo] = await Promise.all([
          api.get("/analytics/summary"),
          api.get("/analytics/mechanics", { params: { month: currentMonth } }).catch(() => ({ data: [] })),
          api.get("/analytics/vehicles").catch(() => ({ data: [] })),
          api.get("/settings").catch(() => ({ data: {} })),
          api.get("/work-orders", { params: { limit: 10 } }).catch(() => ({ data: [] })),
        ]);
        setSummary(s.data);
        setMechanics(m.data || []);
        setVehicles(v.data || []);
        setSettings(cfg.data || {});
        setWorkOrders(Array.isArray(wo.data) ? wo.data : (wo.data?.items || []));
        if (isAdmin) {
          const p = await api.get("/analytics/pool", { params: { month: currentMonth } }).catch(() => ({ data: {} }));
          setPool(p.data || {});
        }
        // Notlar — açıklar
        const nres = await api.get("/notes", { params: { open_only: true, limit: 8 } }).catch(() => ({ data: [] }));
        setNotes(Array.isArray(nres.data) ? nres.data : (nres.data?.items || []));
        // Bugünün vardiyası
        const sh = await api.get("/shifts", { params: { date: today } }).catch(() => ({ data: [] }));
        setShiftsToday(Array.isArray(sh.data) ? sh.data : (sh.data?.rows || []));
        // Son kaza raporları
        const acc = await api.get("/accidents", { params: { limit: 5 } }).catch(() => ({ data: [] }));
        setAccidents(Array.isArray(acc.data) ? acc.data : []);
        if (isDE) {
          const dw = await api.get("/drivers/document-warnings").catch(() => ({ data: {} }));
          const list = Array.isArray(dw.data?.items) ? dw.data.items : (Array.isArray(dw.data) ? dw.data : []);
          setDocWarnings(list.slice(0, 8));
          // Bugün için işaretlenmemiş saha görevi var mı?
          const fc = await api.get("/field-supervisors/checklist", { params: { month: currentMonth } }).catch(() => ({ data: [] }));
          const arr = Array.isArray(fc.data) ? fc.data : (fc.data?.items || []);
          const rec = arr.find((r) => (r.date || "").slice(0, 10) === today);
          setUnmarkedField(!rec);
        }
      } catch { /* silent */ }
    })();
  }, [role, isAdmin, isDE]);

  const threshold = settings?.critical_repeat_threshold || 3;
  const topMechs = useMemo(() => mechanics.slice(0, 6), [mechanics]);
  const criticalVehicles = useMemo(
    () => vehicles.filter((v) => (v.repeat_count || 0) >= threshold),
    [vehicles, threshold]
  );
  const maxMechPoint = useMemo(
    () => Math.max(1, ...mechanics.slice(0, 6).map((m) => m.total_points || 0)),
    [mechanics]
  );
  const monthLabel = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
  }, []);

  // Bugünün vardiya özeti — hat bazında araç sayısı
  const shiftSummary = useMemo(() => {
    if (!shiftsToday.length) return null;
    const byLine = new Map();
    for (const r of shiftsToday) {
      const line = (r.hat_arac || r.line || "—").split(" ")[0] || "—";
      const cur = byLine.get(line) || { line, vehicles: 0, drivers: new Set() };
      cur.vehicles += 1;
      if (r.shift1_personnel) cur.drivers.add(r.shift1_personnel);
      if (r.shift2_personnel) cur.drivers.add(r.shift2_personnel);
      byLine.set(line, cur);
    }
    return [...byLine.values()].map((x) => ({ line: x.line, vehicles: x.vehicles, drivers: x.drivers.size }));
  }, [shiftsToday]);

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* ── Page header ── */}
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <h1 style={{ fontFamily: "'Barlow Semi Condensed', sans-serif", fontWeight: 600, fontSize: 28, letterSpacing: "-0.01em", color: "var(--y-text)" }}>
            Kontrol Paneli
          </h1>
          <p className="mt-1" style={{ fontSize: 13, color: "var(--y-muted)" }}>
            {isAdmin ? "Atölye verimliliği, kronik arıza ve prim hak edişleri." : "Atölye ve saha operasyon özeti."}
          </p>
        </div>
        <div className="mono px-3 h-8 inline-flex items-center rounded-full"
          style={{ background: "var(--y-surface)", border: "1px solid var(--y-border)", fontSize: 12, color: "var(--y-text-2)" }}>
          {monthLabel}
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testId="kpi-monthly-orders" label="Bu Ay İş Emri" value={fmtInt(summary?.monthly_orders)} hint={monthLabel} Icon={TrendingUp} tone="accent" />
        <Kpi testId="kpi-open-orders" label="Açık / Devam Eden" value={fmtInt(summary?.open_orders)} hint="Sırada / devam" Icon={ClipboardList} tone="warning" />
        <Kpi testId="kpi-monthly-repeat" label="Bu Ay Tekrar Arıza" value={fmtInt(summary?.monthly_repeat_faults)} hint={`Eşik: ${threshold}+`} Icon={Repeat2} tone="warning" />
        {isAdmin ? (
          <Kpi testId="kpi-monthly-bonus" label="Havuz Bütçesi" value={fmtTl(settings?.monthly_pool_budget ?? 50000)} hint="Aylık usta primi" Icon={Coins} tone="success" />
        ) : (
          <Kpi testId="kpi-active-vehicles" label="Aktif Araç" value={fmtInt(summary?.active_vehicles)} hint="Filoda faal" Icon={Bus} tone="success" />
        )}
      </div>

      {/* ── data_entry ek: belge uyarıları + saha görev hatırlatma ── */}
      {isDE && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="y-card overflow-hidden" data-testid="de-doc-warnings-card">
            <div className="px-5 py-4" style={{ borderBottom: "1px solid var(--y-border)" }}>
              <div style={{ fontSize: 18, fontWeight: 600, color: "var(--y-text)", fontFamily: "'Barlow Semi Condensed', sans-serif" }}>
                Belgesi dolan / yakın şoförler
              </div>
              <div style={{ fontSize: 12, color: "var(--y-muted)" }}>{docWarnings.length} şoför</div>
            </div>
            <div className="p-4">
              {docWarnings.length === 0 ? (
                <div className="text-center py-6" style={{ color: "var(--y-muted)", fontSize: 13 }}>Uyarı yok.</div>
              ) : (
                <ul className="space-y-2 text-sm">
                  {docWarnings.map((d, i) => (
                    <li key={i} data-testid={`doc-warn-${i}`} className="flex justify-between">
                      <span style={{ color: "var(--y-text)" }}>{d.name || d.driver_name || d.full_name || "—"}</span>
                      <span className="mono" style={{ color: d.days_left <= 0 ? DANGER : WARN }}>
                        {d.document_type || d.doc || "—"} · {d.expires_at ? new Date(d.expires_at).toLocaleDateString("tr-TR") : "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="y-card overflow-hidden" data-testid="de-field-reminder-card">
            <div className="px-5 py-4" style={{ borderBottom: "1px solid var(--y-border)" }}>
              <div style={{ fontSize: 18, fontWeight: 600, color: "var(--y-text)", fontFamily: "'Barlow Semi Condensed', sans-serif" }}>
                Günlük Saha Görevleri
              </div>
              <div style={{ fontSize: 12, color: "var(--y-muted)" }}>Bugün için durum</div>
            </div>
            <div className="p-6 text-center">
              {unmarkedField === null ? (
                <div style={{ color: "var(--y-muted)", fontSize: 13 }}>Yükleniyor…</div>
              ) : unmarkedField ? (
                <>
                  <ClipboardCheck className="w-8 h-8 mx-auto mb-2" style={{ color: WARN }} />
                  <div style={{ color: "var(--y-text)", fontSize: 14, fontWeight: 600 }}>Bugün görevler işaretlenmemiş</div>
                  <Link to="/saha-gorevler" className="mt-3 inline-block text-[13px]" style={{ color: "var(--y-accent)" }}>Şimdi işaretle →</Link>
                </>
              ) : (
                <>
                  <ClipboardCheck className="w-8 h-8 mx-auto mb-2" style={{ color: SUCCESS }} />
                  <div style={{ color: "var(--y-text)", fontSize: 14, fontWeight: 600 }}>Bugün için giriş var</div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Kritik araçlar + (Ustalar veya vardiya özeti) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 y-card overflow-hidden" data-testid="critical-vehicles-card">
          <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--y-border)" }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-[8px] flex items-center justify-center"
                style={{ background: "rgba(255,92,92,0.14)", color: DANGER }}>
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <div style={{ fontSize: 20, fontWeight: 600, color: "var(--y-text)", fontFamily: "'Barlow Semi Condensed', sans-serif" }}>Kritik araçlar</div>
                <div style={{ fontSize: 12, color: "var(--y-muted)" }}>{criticalVehicles.length} araç {threshold}+ tekrar arıza eşiğinde</div>
              </div>
            </div>
            <Link to="/kronik" className="text-[13px]" style={{ color: "var(--y-accent)" }} data-testid="critical-view-all">Tümü →</Link>
          </div>
          <div className="p-2">
            {criticalVehicles.length === 0 ? (
              <div className="py-10 text-center" style={{ color: "var(--y-muted)", fontSize: 13 }}>Kritik eşiği aşan araç yok.</div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr>
                  <th className="text-left px-3 py-2">Plaka</th>
                  <th className="text-left px-3 py-2">Toplam</th>
                  <th className="text-left px-3 py-2">Tekrar</th>
                  <th className="text-right px-3 py-2">Durum</th>
                </tr></thead>
                <tbody>
                  {criticalVehicles.slice(0, 6).map((v) => (
                    <tr key={v.plate} data-testid={`critical-${v.plate}`}>
                      <td className="px-3 py-2.5">
                        <Link to={`/arac/${encodeURIComponent(v.plate)}`} className="mono inline-flex items-center gap-2" style={{ color: "var(--y-text)", fontWeight: 600 }}>
                          <Bus className="w-4 h-4" style={{ color: DANGER }} /> {v.plate}
                        </Link>
                      </td>
                      <td className="px-3 py-2.5 mono" style={{ color: "var(--y-text-2)" }}>{v.job_count}</td>
                      <td className="px-3 py-2.5 mono" style={{ color: DANGER, fontWeight: 700 }}>{v.repeat_count}</td>
                      <td className="px-3 py-2.5 text-right"><span className="y-chip y-chip-danger">RİSK</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="y-card overflow-hidden" data-testid="mechanics-card">
          <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--y-border)" }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-[8px] flex items-center justify-center" style={{ background: "rgba(255,122,26,0.14)", color: ACCENT }}>
                <Wrench className="w-5 h-5" />
              </div>
              <div>
                <div style={{ fontSize: 20, fontWeight: 600, color: "var(--y-text)", fontFamily: "'Barlow Semi Condensed', sans-serif" }}>Ustalar</div>
                <div style={{ fontSize: 12, color: "var(--y-muted)" }}>{monthLabel}</div>
              </div>
            </div>
            {isAdmin && <Link to="/prim-hakedis" className="text-[13px]" style={{ color: "var(--y-accent)" }}>Tümü →</Link>}
          </div>
          <div className="p-4 space-y-3">
            {topMechs.length === 0 && (
              <div className="py-6 text-center" style={{ color: "var(--y-muted)", fontSize: 13 }}>Bu ay için usta puanı yok.</div>
            )}
            {topMechs.map((m) => {
              const pct = Math.max(4, ((m.total_points || 0) / maxMechPoint) * 100);
              return (
                <div key={m.name} data-testid={`mech-row-${m.name}`}>
                  <div className="flex items-baseline justify-between text-sm mb-1">
                    <span style={{ color: "var(--y-text)", fontWeight: 600 }}>{m.name}</span>
                    <span className="mono" style={{ color: "var(--y-text)", fontWeight: 600 }}>
                      {isAdmin ? fmtTl(m.projected_tl ?? m.tl ?? 0) : `${(m.total_points || 0).toFixed(1)} pt`}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full" style={{ background: "var(--y-surface-2)" }}>
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: ACCENT }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Son iş emirleri + Notlar ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 y-card overflow-hidden" data-testid="recent-orders-card">
          <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--y-border)" }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 600, color: "var(--y-text)", fontFamily: "'Barlow Semi Condensed', sans-serif" }}>Son iş emirleri</div>
              <div style={{ fontSize: 12, color: "var(--y-muted)" }}>En yeni 10 kayıt</div>
            </div>
            <Link to="/is-emirleri" className="text-[13px]" style={{ color: "var(--y-accent)" }}>Tümü →</Link>
          </div>
          <div>
            {workOrders.length === 0 ? (
              <div className="py-10 text-center" style={{ color: "var(--y-muted)", fontSize: 13 }}>Kayıt yok.</div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr>
                  <th className="text-left px-4 py-2">Tarih</th>
                  <th className="text-left px-4 py-2">Plaka</th>
                  <th className="text-left px-4 py-2">Sistem · Parça · Konum</th>
                  <th className="text-left px-4 py-2">Usta</th>
                  <th className="text-right px-4 py-2">Puan</th>
                </tr></thead>
                <tbody>
                  {workOrders.slice(0, 10).map((w, i) => {
                    const items = Array.isArray(w.items) ? w.items : [];
                    const firstItem = items[0] || {};
                    const locs = (Array.isArray(firstItem.locations) && firstItem.locations.length ? firstItem.locations : [firstItem.location || ""]).filter(Boolean).join(", ");
                    const parts = [firstItem.system_name, firstItem.part_name, locs].filter(Boolean).join(" · ");
                    return (
                      <tr key={w.id || i} data-testid={`wo-row-${i}`}>
                        <td className="px-4 py-2.5 mono" style={{ color: "var(--y-muted)" }}>{fmtDate(w.date || w.entry_date || w.created_at)}</td>
                        <td className="px-4 py-2.5 mono" style={{ color: "var(--y-text)", fontWeight: 600 }}>{w.vehicle_plate || w.plate || "—"}</td>
                        <td className="px-4 py-2.5" style={{ color: "var(--y-text-2)" }}>{parts || "—"}</td>
                        <td className="px-4 py-2.5" style={{ color: "var(--y-text-2)" }}>{w.primary_mechanic_name || w.mechanic_name || "—"}</td>
                        <td className="px-4 py-2.5 text-right mono" style={{ color: "var(--y-text)", fontWeight: 600 }}>{(w.primary_points ?? 0).toFixed(1)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="lg:col-span-1">
          <OperationalNotesWidget />
        </div>
      </div>

      {/* ── Bugünün vardiya özeti + Son kaza raporları ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="y-card overflow-hidden" data-testid="today-shifts-card">
          <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--y-border)" }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-[8px] flex items-center justify-center" style={{ background: "rgba(139,156,255,0.14)", color: INDIGO }}>
                <CalendarDays className="w-5 h-5" />
              </div>
              <div>
                <div style={{ fontSize: 20, fontWeight: 600, color: "var(--y-text)", fontFamily: "'Barlow Semi Condensed', sans-serif" }}>Bugünün Vardiyası</div>
                <div style={{ fontSize: 12, color: "var(--y-muted)" }}>{new Date().toLocaleDateString("tr-TR")}</div>
              </div>
            </div>
            <Link to="/vardiya" className="text-[13px]" style={{ color: "var(--y-accent)" }}>Aç →</Link>
          </div>
          <div className="p-4">
            {!shiftSummary ? (
              <div className="text-center py-6" style={{ color: "var(--y-muted)", fontSize: 13 }} data-testid="no-shift-today">
                Bugün için vardiya girilmedi
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr>
                  <th className="text-left px-2 py-1">Hat</th>
                  <th className="text-right px-2 py-1">Araç</th>
                  <th className="text-right px-2 py-1">Şoför</th>
                </tr></thead>
                <tbody>
                  {shiftSummary.map((s, i) => (
                    <tr key={i} data-testid={`shift-line-${s.line}`}>
                      <td className="px-2 py-1.5 mono" style={{ color: "var(--y-text)" }}>{s.line}</td>
                      <td className="px-2 py-1.5 mono text-right" style={{ color: "var(--y-text-2)" }}>{s.vehicles}</td>
                      <td className="px-2 py-1.5 mono text-right" style={{ color: "var(--y-text-2)" }}>{s.drivers}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="y-card overflow-hidden" data-testid="recent-accidents-card">
          <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--y-border)" }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-[8px] flex items-center justify-center" style={{ background: "rgba(242,201,76,0.14)", color: WARN }}>
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <div style={{ fontSize: 20, fontWeight: 600, color: "var(--y-text)", fontFamily: "'Barlow Semi Condensed', sans-serif" }}>Son Kaza Raporları</div>
                <div style={{ fontSize: 12, color: "var(--y-muted)" }}>En yeni 5</div>
              </div>
            </div>
            {isAdmin && <Link to="/kaza-raporlari" className="text-[13px]" style={{ color: "var(--y-accent)" }}>Tümü →</Link>}
          </div>
          <div className="p-4">
            {accidents.length === 0 ? (
              <div className="text-center py-6" style={{ color: "var(--y-muted)", fontSize: 13 }}>Kayıt yok.</div>
            ) : (
              <ul className="space-y-2 text-sm">
                {accidents.slice(0, 5).map((a, i) => (
                  <li key={a.id || i} data-testid={`acc-${i}`} className="flex justify-between">
                    <span style={{ color: "var(--y-text)" }}>
                      <span className="mono mr-2" style={{ color: "var(--y-muted)" }}>{fmtDate(a.date)}</span>
                      {a.vehicle_plate || a.plate || "—"} · {a.driver_name || "—"}
                    </span>
                    <span className="mono" style={{ color: "var(--y-muted)" }}>{a.status || "—"}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ── Usta verim grafiği (herkes görebilir; puan bazında) ── */}
      <Card className="y-card border-0" data-testid="chart-mechanic-productivity">
        <CardHeader style={{ borderBottom: "1px solid var(--y-border)" }}>
          <div className="flex items-center justify-between">
            <div>
              <div style={{ fontSize: 11, color: "var(--y-label)", letterSpacing: "0.1em", fontWeight: 600, textTransform: "uppercase" }}>Verimlilik</div>
              <CardTitle style={{ fontSize: 20, fontFamily: "'Barlow Semi Condensed', sans-serif", color: "var(--y-text)", marginTop: 4 }}>Usta Puan Karşılaştırması</CardTitle>
            </div>
            <Users className="w-4 h-4" style={{ color: "var(--y-muted)" }} />
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={topMechs} margin={{ top: 10, right: 12, left: 0, bottom: 30 }} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--y-border)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--y-muted)" }} axisLine={false} tickLine={false} angle={-15} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11, fill: "var(--y-muted)" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "rgba(255,122,26,0.06)" }} contentStyle={{ background: "var(--y-surface)", border: "1px solid var(--y-border)", borderRadius: 10, fontSize: 12, color: "var(--y-text)" }} />
              <Bar dataKey="total_points" name="Toplam Puan" radius={[6, 6, 0, 0]} maxBarSize={48}>
                {topMechs.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── KPI card ── */
function Kpi({ label, value, hint, Icon, tone = "neutral", testId }) {
  const toneMap = {
    neutral: { bg: "rgba(155,163,174,0.10)", fg: "var(--y-muted)" },
    accent:  { bg: "rgba(255,122,26,0.14)", fg: ACCENT },
    success: { bg: "rgba(61,214,140,0.14)",  fg: SUCCESS },
    warning: { bg: "rgba(242,201,76,0.14)",  fg: WARN },
  };
  const t = toneMap[tone] || toneMap.neutral;
  return (
    <div className="y-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div style={{ fontSize: 13, color: "var(--y-muted)", fontWeight: 500 }}>{label}</div>
          <div className="metric-num mt-3" style={{ fontFamily: "'Barlow Semi Condensed', sans-serif", fontWeight: 700, fontSize: 40, lineHeight: 1, color: "var(--y-text)" }}>
            {value}
          </div>
          <div className="mt-2" style={{ fontSize: 13, color: "var(--y-muted)" }}>{hint}</div>
        </div>
        <div className="w-10 h-10 rounded-[10px] shrink-0 flex items-center justify-center" style={{ background: t.bg, color: t.fg }}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}
